<!-- app/javascript/dashboard/routes/dashboard/bulkmessage/components/BroadcastHistory.vue -->
<script setup>
import { ref, computed, onMounted, watch, defineEmits, defineProps } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import axios from 'axios';
import Button from 'dashboard/components-next/button/Button.vue';
import RetryBroadcastModal from './modals/RetryBroadcastModal.vue';
import ContactsPagination from './ContactsPagination.vue';
import ScheduledBroadcastsAPI from 'dashboard/api/scheduledBroadcasts';
import useBroadcastProgressChannel from '../composables/useBroadcastProgressChannel';

const emit = defineEmits(['view-details', 'navigate-new']);
const props = defineProps({
  showScheduledOnly: {
    type: Boolean,
    default: false,
  },
});

const router = useRouter();
const route = useRoute();
const store = useStore();

// State
const broadcasts = ref([]);
const scheduledBroadcasts = ref([]);
const isLoading = ref(true);
const isLoadingScheduled = ref(true);
const searchQuery = ref('');
const notification = ref({ show: false, message: '', type: 'info' });
const showRetryModal = ref(false);
const selectedBroadcast = ref(null);
const scheduledSectionRef = ref(null);
const {
  subscribeToBroadcast,
  unsubscribeFromBroadcast,
  resetSubscriptions,
} = useBroadcastProgressChannel();

// Pagination
const pagination = ref({
  currentPage: 1,
  pageSize: 25,
  totalPages: 1,
});
const totalCount = ref(0);
const scheduledPagination = ref({
  currentPage: 1,
  pageSize: 25,
  totalPages: 1,
});
const scheduledTotalCount = ref(0);
const pageSizeOptions = [10, 25, 50, 100, 500, 1000];

// Show notification helper
const showNotification = (message, type = 'info') => {
  notification.value = { show: true, message, type };
  setTimeout(() => {
    notification.value.show = false;
  }, 5000);
};

// Get authentication headers from cookie
const getAuthHeaders = () => {
  const getCookie = name => {
    const match = document.cookie.match(
      new RegExp('(^| )' + name + '=([^;]+)')
    );
    return match ? decodeURIComponent(match[2]) : null;
  };

  const cookie = getCookie('cw_d_session_info');
  if (!cookie) return {};
  try {
    const sessionInfo = JSON.parse(cookie);
    return {
      'access-token': sessionInfo['access-token'],
      client: sessionInfo.client,
      uid: sessionInfo.uid,
    };
  } catch (e) {
    console.error('Erro ao ler o cookie:', e);
    return {};
  }
};

// Computed properties
const filteredBroadcasts = computed(() => {
  if (!searchQuery.value) return broadcasts.value;

  const query = searchQuery.value.toLowerCase();

  return broadcasts.value.filter(broadcast => {
    // Search in integration type
    const formatted = getInboxTypeFormatted(broadcast.integration_type);
    if (formatted.displayName.toLowerCase().includes(query)) {
      return true;
    }

    // Search in status
    if (formatStatus(broadcast.status).toLowerCase().includes(query)) {
      return true;
    }

    // Search in template name
    if (
      broadcast.template_name &&
      broadcast.template_name.toLowerCase().includes(query)
    ) {
      return true;
    }

    // Search in date
    if (formatDate(broadcast.created_at).toLowerCase().includes(query)) {
      return true;
    }

    return false;
  });
});

const filteredScheduledBroadcasts = computed(() => {
  if (!searchQuery.value) return scheduledBroadcasts.value;

  const query = searchQuery.value.toLowerCase();

  return scheduledBroadcasts.value.filter(broadcast => {
    if (formatStatus(broadcast.status).toLowerCase().includes(query)) {
      return true;
    }

    if (
      formatDate(broadcast.scheduled_at).toLowerCase().includes(query) ||
      formatTime(broadcast.scheduled_at).toLowerCase().includes(query)
    ) {
      return true;
    }

    if (
      (broadcast.content || '')
        .toString()
        .toLowerCase()
        .includes(query)
    ) {
      return true;
    }

    return false;
  });
});

// Methods
const fetchBroadcasts = async () => {
  isLoading.value = true;

  try {
    const response = await axios.get('/api/v1/bulk_messages', {
      params: {
        page: pagination.value.currentPage,
        per_page: pagination.value.pageSize,
      },
      headers: getAuthHeaders(),
    });

    broadcasts.value = response.data.broadcasts || [];

    // Update pagination
    if (response.data.meta) {
      pagination.value.currentPage = response.data.meta.current_page;
      pagination.value.totalPages = response.data.meta.total_pages;
      totalCount.value = response.data.meta.total_count;
    }
  } catch (error) {
    console.error('Error fetching broadcasts:', error);
    showNotification('Erro ao carregar histórico', 'error');
  } finally {
    isLoading.value = false;
  }
};

const fetchScheduledBroadcasts = async () => {
  isLoadingScheduled.value = true;
  const minDelay = new Promise(resolve => setTimeout(resolve, 500));
  try {
    const response = await ScheduledBroadcastsAPI.getScheduledBroadcasts(
      scheduledPagination.value.currentPage,
      scheduledPagination.value.pageSize
    );
    scheduledBroadcasts.value =
      response.data.scheduled_broadcasts?.map(broadcast => ({
        ...broadcast,
        isScheduled: true,
      })) || [];
    if (response.data.meta) {
      scheduledPagination.value.currentPage =
        response.data.meta.current_page || scheduledPagination.value.currentPage;
      scheduledPagination.value.totalPages =
        response.data.meta.total_pages || scheduledPagination.value.totalPages;
      scheduledPagination.value.pageSize =
        response.data.meta.per_page || scheduledPagination.value.pageSize;
      scheduledTotalCount.value = response.data.meta.total_count || 0;
    } else {
      scheduledTotalCount.value = scheduledBroadcasts.value.length;
    }
    registerProgressListeners();
  } catch (error) {
    console.error('Error fetching scheduled broadcasts:', error);
    showNotification('Erro ao carregar disparos agendados', 'error');
  } finally {
    await minDelay;
    isLoadingScheduled.value = false;
  }
};
const registerProgressListeners = () => {
  resetSubscriptions();
  scheduledBroadcasts.value.forEach(broadcast => {
    if (broadcast.status === 'processing' || broadcast.status === 'pending') {
      console.info('[BroadcastHistory] Subscribing to progress', {
        id: broadcast.id,
        status: broadcast.status
      });
      subscribeToBroadcast(broadcast.id, handleProgressUpdate);
    } else {
      unsubscribeFromBroadcast(broadcast.id);
    }
  });
};

const handleProgressUpdate = payload => {
  console.info('[BroadcastHistory] Progress update received', payload);
  if (!payload || !payload.scheduled_message_id) {
    return;
  }

  const id = Number(payload.scheduled_message_id);
  const index = scheduledBroadcasts.value.findIndex(
    item => Number(item.id) === id
  );
  if (index === -1) {
    fetchScheduledBroadcasts();
    return;
  }

  const existing = scheduledBroadcasts.value[index];
  const updated = {
    ...existing,
    progress: payload.progress || existing.progress,
    status: payload.status || existing.status,
  };
  scheduledBroadcasts.value.splice(index, 1, updated);

  if (updated.status && updated.status !== 'processing') {
    unsubscribeFromBroadcast(id);
    // refresh list to sync counts and status
    fetchScheduledBroadcasts();
    fetchBroadcasts();
  }
};

const goToPage = page => {
  pagination.value.currentPage = page;
  fetchBroadcasts();
};

const handlePageChange = page => {
  pagination.value.currentPage = page;
  fetchBroadcasts();
};

const handlePageSizeChange = newSize => {
  pagination.value.pageSize = newSize;
  pagination.value.currentPage = 1; // Reset to first page when changing page size
  fetchBroadcasts();
};

const handleScheduledPageChange = page => {
  scheduledPagination.value.currentPage = page;
  scrollToScheduledTop();
  fetchScheduledBroadcasts();
};

const handleScheduledPageSizeChange = newSize => {
  scheduledPagination.value.pageSize = newSize;
  scheduledPagination.value.currentPage = 1;
  scrollToScheduledTop();
  fetchScheduledBroadcasts();
};

const scrollToScheduledTop = () => {
  if (scheduledSectionRef.value?.scrollIntoView) {
    scheduledSectionRef.value.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

const cancelBroadcast = async id => {
  try {
    await axios.post(
      `/api/v1/bulk_messages/${id}/cancel`,
      {},
      {
        headers: getAuthHeaders(),
      }
    );

    showNotification('Disparo cancelado', 'success');

    // Refresh the list
    fetchBroadcasts();
  } catch (error) {
    console.error('Error cancelling broadcast:', error);
    showNotification('Erro ao cancelar', 'error');
  }
};

const cancelScheduledBroadcast = async id => {
  const broadcast = scheduledBroadcasts.value.find(item => item.id === id);
  if (!broadcast || new Date(broadcast.scheduled_at) <= new Date()) {
    showNotification('Somente disparos futuros podem ser cancelados', 'warning');
    return;
  }
  try {
    await ScheduledBroadcastsAPI.cancelScheduledBroadcast(id);
    showNotification('Agendamento cancelado', 'success');
    fetchScheduledBroadcasts();
  } catch (error) {
    console.error('Error cancelling scheduled broadcast:', error);
    showNotification('Erro ao cancelar agendamento', 'error');
  }
};

const editScheduledBroadcast = broadcast => {
  emit('navigate-new', { scheduledBroadcast: broadcast, mode: 'edit' });
  showNotification('Funcionalidade de edição ainda em desenvolvimento.', 'warning');
};

const resendScheduledBroadcast = async broadcast => {
  try {
    await ScheduledBroadcastsAPI.cancelScheduledBroadcast(broadcast.id);
    emit('navigate-new', {
      scheduledBroadcast: broadcast,
      mode: 'resend'
    });
    showNotification('Agendamento enviado para tela de envio imediato.', 'info');
  } catch (error) {
    console.error('Error resending scheduled broadcast:', error);
    showNotification('Erro ao reenviar agendamento', 'error');
  }
};

const retryBroadcast = async id => {
  try {
    // First fetch the broadcast details
    const response = await axios.get(`/api/v1/bulk_messages/${id}`, {
      headers: getAuthHeaders(),
    });

    selectedBroadcast.value = response.data;
    showRetryModal.value = true;
  } catch (error) {
    console.error('Error fetching broadcast for retry:', error);
    showNotification('Erro ao carregar detalhes do disparo', 'error');
  }
};

const handleRetryProceed = data => {
  // Close the modal
  showRetryModal.value = false;

  // Navigate to BulkMessageNew with the selected recipients
  const queryParams = {
    recipients: JSON.stringify(data.recipients),
    message: data.broadcast.message,
    broadcastId: data.broadcast.id,
  };

  // Add template info if present
  if (data.broadcast.template_name) {
    queryParams.templateName = data.broadcast.template_name;
    queryParams.templateId = data.broadcast.template_id;
  }

  router.push({
    name: 'bulkMessage_new',
    query: queryParams,
  });
};

const viewBroadcastDetails = id => {
  // Emit the event for components that might be listening
  emit('view-details', id);

  // ALSO directly navigate to the details page
  router.push({
    name: 'bulkMessage_details',
    params: { id: id },
  });
};

const navigateToNewBroadcast = () => {
  emit('navigate-new');
};

// Helper functions
const formatDate = dateString => {
  if (!dateString) return '';

  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const formatTime = dateString => {
  if (!dateString) return '';

  const date = new Date(dateString);
  return date.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Use store getter for integration type formatting
const getInboxTypeFormatted = type => {
  return store.getters['inboxMetadata/formatIntegrationType'](type);
};

const getInboxIdFromBroadcast = broadcast => {
  if (!broadcast) return null;
  if (broadcast.inbox_id) return broadcast.inbox_id;
  if (broadcast.inbox && broadcast.inbox.id) return broadcast.inbox.id;

  const payload = broadcast.target_payload;
  if (payload) {
    try {
      const parsed = typeof payload === 'string' ? JSON.parse(payload) : payload;
      if (parsed && parsed.inbox_id) {
        return parsed.inbox_id;
      }
    } catch (error) {
      console.warn('Erro ao ler target_payload do agendamento', error);
    }
  }

  return null;
};

const getScheduledInboxFormatted = broadcast => {
  const inboxId = getInboxIdFromBroadcast(broadcast);
  if (inboxId) {
    const formatted = store.getters['inboxMetadata/getInboxFormatted'](inboxId);
    if (formatted) return formatted;
  }
  return getInboxTypeFormatted(broadcast.integration_type);
};

const formatStatus = status => {
  if (!status) return 'Desconhecido';

  const statuses = {
    pending: 'Pendente',
    completed: 'Concluído',
    failed: 'Falhou',
    cancelled: 'Cancelado',
    sent: 'Enviado',
  };

  return statuses[status] || status;
};

const getStatusClass = broadcast => {
  const status = broadcast.status;

  if (!status) return 'text-n-slate-9 dark:text-n-slate-10';

  const classes = {
    pending: 'text-n-amber-11 dark:text-n-amber-10',
    completed: 'text-green-600 dark:text-green-400',
    failed: 'text-n-ruby-11 dark:text-n-ruby-10',
    cancelled: 'text-n-ruby-11 dark:text-n-ruby-10',
  };

  return classes[status] || 'text-n-slate-9 dark:text-n-slate-10';
};

const getProgressBarClass = broadcast => {
  const status = broadcast.status;

  if (!status) return 'bg-n-slate-6 dark:bg-n-slate-7';

  const classes = {
    pending: 'bg-green-500 dark:bg-green-600',
    completed: 'bg-green-500 dark:bg-green-600',
    failed: 'bg-green-500 dark:bg-green-600',
    cancelled: 'bg-green-500 dark:bg-green-600',
  };

  return classes[status] || 'bg-n-slate-6 dark:bg-n-slate-7';
};

const formatScheduledDateTime = (datetime, timezone) => {
  if (!datetime) return '';

  try {
    return new Intl.DateTimeFormat('pt-BR', {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: timezone || 'America/Sao_Paulo',
    }).format(new Date(datetime));
  } catch (error) {
    return `${formatDate(datetime)} ${formatTime(datetime)}`;
  }
};

const getScheduledRecipientsCount = broadcast => {
  const payload = broadcast.target_payload || {};
  if (Array.isArray(payload.recipients)) {
    return payload.recipients.length;
  }
  if (Array.isArray(payload.contact_ids)) {
    return payload.contact_ids.length;
  }
  return 0;
};

const hasScheduledAttachments = broadcast => {
  return Array.isArray(broadcast.attachment_ids) && broadcast.attachment_ids.length > 0;
};

const getScheduledStatusBadgeClasses = status => {
  if (!status) return 'bg-n-slate-3 text-n-slate-11';

  const map = {
    pending: 'bg-n-iris-2 text-n-iris-11 border border-n-iris-6',
    failed: 'bg-n-ruby-2 text-n-ruby-11 border border-n-ruby-6',
    cancelled: 'bg-n-slate-2 text-n-slate-11 border border-n-slate-6',
    sent: 'bg-n-grass-2 text-n-grass-11 border border-n-grass-6',
  };

  return map[status] || 'bg-n-slate-3 text-n-slate-11';
};

// Helper methods to extract counts from JSON
const getRecipientCount = broadcast => {
  if (!broadcast.total_recipients) return 0;

  try {
    return JSON.parse(broadcast.total_recipients).length;
  } catch (e) {
    return 0;
  }
};

const getSuccessCount = broadcast => {
  if (!broadcast.successful_phones) return 0;

  try {
    return JSON.parse(broadcast.successful_phones).length;
  } catch (e) {
    return 0;
  }
};

const getFailureCount = broadcast => {
  if (!broadcast.failed_phones) return 0;

  try {
    return JSON.parse(broadcast.failed_phones).length;
  } catch (e) {
    return 0;
  }
};

const getCompletionPercentage = broadcast => {
  const total = getRecipientCount(broadcast);
  if (total === 0) return 0;

  const processed = getSuccessCount(broadcast) + getFailureCount(broadcast);
  return Math.round((processed / total) * 100);
};

// Watch for search query changes to refresh filtered broadcasts
watch(searchQuery, () => {
  // No need to refetch, just filter client-side
});

watch(
  () =>
    scheduledBroadcasts.value.map(broadcast => ({
      id: broadcast.id,
      status: broadcast.status,
    })),
  () => {
    registerProgressListeners();
  },
  { deep: true }
);

const ensureInboxMetadata = async () => {
  if (!store.getters['inboxMetadata/isMetadataLoaded']) {
    try {
      await store.dispatch('inboxMetadata/loadAllMetadata');
    } catch (error) {
      console.error('Erro ao carregar metadata de inbox', error);
    }
  }
};

// Lifecycle hooks
onMounted(async () => {
  if (props.showScheduledOnly) {
    await ensureInboxMetadata();
    isLoading.value = false;
    fetchScheduledBroadcasts();
    return;
  }

  fetchBroadcasts();
  isLoadingScheduled.value = false;
});
</script>

<template>
  <div class="broadcast-history">
    <!-- Beautiful Notification -->
    <Transition name="slide-down" mode="out-in">
      <div
        v-if="notification.show"
        class="fixed top-4 right-4 left-4 sm:left-auto z-50 max-w-md w-auto sm:w-full"
      >
        <div
          class="rounded-xl p-4 shadow-lg border backdrop-blur-sm transition-all duration-300"
          :class="[
            notification.type === 'success'
              ? 'bg-green-50/90 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-800 dark:text-green-300'
              : '',
            notification.type === 'error'
              ? 'bg-n-ruby-2/90 dark:bg-n-ruby-3/20 border-n-ruby-6 dark:border-n-ruby-8 text-n-ruby-12 dark:text-n-ruby-11'
              : '',
            notification.type === 'warning'
              ? 'bg-n-amber-2/90 dark:bg-n-amber-3/20 border-n-amber-6 dark:border-n-amber-8 text-n-amber-12 dark:text-n-amber-11'
              : '',
            notification.type === 'info'
              ? 'bg-n-iris-2/90 dark:bg-n-iris-3/20 border-n-iris-6 dark:border-n-iris-8 text-n-iris-12 dark:text-n-iris-11'
              : '',
          ]"
        >
          <div class="flex items-start gap-3">
            <div class="flex-shrink-0 mt-0.5">
              <i
                v-if="notification.type === 'success'"
                class="i-lucide-check-circle text-green-600 dark:text-green-400"
              />
              <i
                v-else-if="notification.type === 'error'"
                class="i-lucide-x-circle text-n-ruby-11 dark:text-n-ruby-10"
              />
              <i
                v-else-if="notification.type === 'warning'"
                class="i-lucide-alert-triangle text-n-amber-11 dark:text-n-amber-10"
              />
              <i v-else class="i-lucide-info text-n-iris-11 dark:text-n-iris-10" />
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium">{{ notification.message }}</p>
            </div>
            <button
              class="flex-shrink-0 opacity-70 hover:opacity-100 transition-opacity"
              @click="notification.show = false"
            >
              <i class="i-lucide-x text-sm" />
            </button>
          </div>
        </div>
      </div>
    </Transition>
    <!-- Scheduled broadcasts -->
    <section v-if="showScheduledOnly" class="mb-8" ref="scheduledSectionRef">
      <div
        class="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
      >
        <div>
          <h2 class="text-lg sm:text-xl font-medium text-n-slate-12 dark:text-n-slate-12">
            Disparos Agendados
          </h2>
          <p class="text-sm text-n-slate-11 dark:text-n-slate-10">
            Agendamentos pendentes e falhos aparecem aqui.
          </p>
        </div>
        <div class="flex items-center gap-2">
          <Button
            label="Atualizar"
            icon="i-lucide-refresh-cw"
            variant="outline"
            color="slate"
            size="sm"
            @click="fetchScheduledBroadcasts"
          />
        </div>
      </div>

      <div
        v-if="isLoadingScheduled && scheduledBroadcasts.length === 0"
        class="flex justify-center items-center gap-3 py-6"
      >
        <div
          class="w-8 h-8 rounded-full border-4 border-n-slate-3 dark:border-n-slate-6 border-t-n-brand dark:border-t-primary-500 animate-spin"
        />
        <p class="text-sm text-n-slate-11 dark:text-n-slate-9">
          Carregando disparos agendados...
        </p>
      </div>

      <div
        v-else-if="filteredScheduledBroadcasts.length === 0"
        class="rounded-xl border border-dashed border-n-weak dark:border-n-slate-6 p-4 text-sm text-n-slate-11 dark:text-n-slate-10"
      >
        Nenhum disparo agendado no momento.
      </div>

      <div v-else class="relative">
        <!-- Mobile view -->
        <div class="block sm:hidden space-y-3">
          <div
            v-for="broadcast in filteredScheduledBroadcasts"
            :key="broadcast.id"
            class="bg-white dark:bg-n-slate-2 border border-n-slate-3 dark:border-n-slate-7 rounded-lg p-4"
          >
            <div class="flex items-start justify-between">
              <div>
                <p class="text-sm font-medium text-n-slate-12 dark:text-n-slate-12">
                  {{ formatScheduledDateTime(broadcast.scheduled_at, broadcast.timezone) }}
                </p>
                <p class="text-xs text-n-slate-11 dark:text-n-slate-10">
                  {{ broadcast.timezone || 'America/Sao_Paulo' }}
                </p>
              </div>
              <span
                class="text-xs px-2 py-0.5 rounded-full border font-medium"
                :class="getScheduledStatusBadgeClasses(broadcast.status)"
              >
                {{ formatStatus(broadcast.status) }}
              </span>
            </div>
            <p class="mt-2 text-sm text-n-slate-12 dark:text-n-slate-12 line-clamp-2">
              <template v-if="broadcast.template_name">
                Template: {{ broadcast.template_name }}
              </template>
              <template v-else>
                {{ broadcast.content || '(Sem conteúdo definido)' }}
              </template>
            </p>
            <div class="mt-2 text-xs text-n-slate-11 dark:text-n-slate-10">
              Destinatários: {{ getScheduledRecipientsCount(broadcast) }}
            </div>
            <div
              v-if="hasScheduledAttachments(broadcast)"
              class="text-xs text-n-slate-11 dark:text-n-slate-10 mt-1"
            >
              Contém mídia
            </div>
            <div class="mt-3 flex gap-2">
              <Button
                label="Cancelar"
                color="ruby"
                variant="outline"
                size="sm"
                icon="i-lucide-trash-2"
                @click="cancelScheduledBroadcast(broadcast.id)"
              />
            </div>
          </div>
        </div>

        <!-- Desktop table -->
        <div
          class="hidden sm:block overflow-x-auto border border-n-slate-3 dark:border-n-slate-7 rounded-lg"
        >
          <table
            class="min-w-full divide-y divide-n-slate-3 dark:divide-n-slate-7"
          >
            <thead class="bg-n-slate-2 dark:bg-n-slate-4">
              <tr>
                <th
                  scope="col"
                  class="px-6 py-3 text-left text-xs font-medium text-n-slate-9 dark:text-n-slate-11 uppercase tracking-wider"
                >
                  Data agendada
                </th>
                <th
                  scope="col"
                  class="px-6 py-3 text-left text-xs font-medium text-n-slate-9 dark:text-n-slate-11 uppercase tracking-wider"
                >
                  Tipo
                </th>
                <th
                  scope="col"
                  class="px-6 py-3 text-left text-xs font-medium text-n-slate-9 dark:text-n-slate-11 uppercase tracking-wider"
                >
                  Destinatários
                </th>
                <th
                  scope="col"
                  class="px-6 py-3 text-left text-xs font-medium text-n-slate-9 dark:text-n-slate-11 uppercase tracking-wider"
                >
                  Conteúdo
                </th>
                <th
                  scope="col"
                  class="px-6 py-3 text-left text-xs font-medium text-n-slate-9 dark:text-n-slate-11 uppercase tracking-wider"
                >
                  Status
                </th>
                <th
                  scope="col"
                  class="px-6 py-3 text-right text-xs font-medium text-n-slate-9 dark:text-n-slate-11 uppercase tracking-wider"
                >
                  Ações
                </th>
              </tr>
            </thead>
            <tbody
              class="bg-white dark:bg-n-slate-2 divide-y divide-n-slate-3 dark:divide-n-slate-7"
            >
              <tr
                v-for="broadcast in filteredScheduledBroadcasts"
                :key="broadcast.id"
                class="hover:bg-n-slate-1 dark:hover:bg-n-slate-3"
              >
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm text-n-slate-12 dark:text-n-slate-12">
                    {{ formatScheduledDateTime(broadcast.scheduled_at, broadcast.timezone) }}
                  </div>
                  <div class="text-xs text-n-slate-9 dark:text-n-slate-11">
                    {{ broadcast.timezone || 'America/Sao_Paulo' }}
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="flex items-center">
                    <i
                      :class="[
                        getScheduledInboxFormatted(broadcast).icon,
                        getScheduledInboxFormatted(broadcast).color,
                        'text-lg'
                      ]"
                    />
                    <div class="ml-2">
                      <div class="text-sm font-medium text-n-slate-12 dark:text-n-slate-12">
                        {{ getScheduledInboxFormatted(broadcast).displayName }}
                      </div>
                      <div
                        v-if="broadcast.template_name"
                        class="text-xs text-n-slate-9 dark:text-n-slate-11"
                      >
                        Template: {{ broadcast.template_name }}
                      </div>
                    </div>
                  </div>
                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm text-n-slate-12 dark:text-n-slate-12">
                    {{ getScheduledRecipientsCount(broadcast) }}
                    destinatário(s)
                  </div>
                  <div
                    v-if="hasScheduledAttachments(broadcast)"
                    class="text-xs text-n-slate-9 dark:text-n-slate-11 mt-1 flex items-center gap-1"
                  >
                    <i class="i-lucide-paperclip text-xs" />
                    Contém mídia
                  </div>
                </td>
                <td class="px-6 py-4">
                  <div class="text-sm text-n-slate-12 dark:text-n-slate-12 line-clamp-2 max-w-md">
                    <template v-if="broadcast.template_name">
                      Template: {{ broadcast.template_name }}
                    </template>
                    <template v-else>
                      {{ broadcast.content || '(Sem conteúdo definido)' }}
                    </template>
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span
                    class="text-xs leading-5 font-medium px-2 py-0.5 rounded-full border"
                    :class="getScheduledStatusBadgeClasses(broadcast.status)"
                  >
                    {{ formatStatus(broadcast.status) }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <Button
                    icon="i-lucide-trash-2"
                    label="Cancelar"
                    color="ruby"
                    variant="outline"
                    size="xs"
                    :disabled="new Date(broadcast.scheduled_at) <= new Date()"
                    @click.stop="cancelScheduledBroadcast(broadcast.id)"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="mt-4" v-if="filteredScheduledBroadcasts.length > 0">
          <ContactsPagination
            :current-page="scheduledPagination.currentPage"
            :total-pages="scheduledPagination.totalPages"
            :page-size="scheduledPagination.pageSize"
            :page-size-options="pageSizeOptions"
            :show-page-size-selector="true"
            :loading="isLoadingScheduled"
            :current-items-count="scheduledBroadcasts.length"
            :total-items-count="scheduledTotalCount"
            item-name="agendamento"
            @page-change="handleScheduledPageChange"
            @page-size-change="handleScheduledPageSizeChange"
          />
        </div>

        <div
          v-if="isLoadingScheduled"
          class="absolute inset-0 bg-white/80 dark:bg-n-slate-2/80 backdrop-blur-sm flex items-center justify-center z-10"
        >
          <div class="flex items-center gap-3">
            <div
              class="w-8 h-8 rounded-full border-4 border-n-slate-3 dark:border-n-slate-6 border-t-n-brand dark:border-t-primary-500 animate-spin"
            />
            <p class="text-sm text-n-slate-11 dark:text-n-slate-9">
              Carregando próxima página...
            </p>
          </div>
        </div>
      </div>
    </section>

    <template v-if="!showScheduledOnly">
      <!-- Loading state -->
      <div v-if="isLoading" class="flex justify-center items-center py-12">
        <div
          class="w-8 h-8 rounded-full border-4 border-n-slate-3 dark:border-n-slate-6 border-t-n-brand dark:border-t-primary-500 animate-spin"
        />
        <p class="ml-4 text-n-slate-11 dark:text-n-slate-10">
          Carregando histórico de disparos...
        </p>
      </div>

      <!-- Empty state -->
      <div
        v-else-if="broadcasts.length === 0"
        class="flex flex-col items-center justify-center py-16"
      >
        <div
          class="w-20 h-20 mb-4 bg-n-slate-2 dark:bg-n-slate-4 rounded-full flex items-center justify-center"
        >
          <i
            class="i-lucide-history text-4xl text-n-slate-9 dark:text-n-slate-11"
          />
        </div>
        <h3 class="text-lg font-medium text-n-slate-12 dark:text-n-slate-12 mb-2">
          Nenhum histórico encontrado
        </h3>
        <p
          class="text-sm text-n-slate-11 dark:text-n-slate-10 text-center max-w-md mb-6"
        >
          Você ainda não realizou nenhum disparo em massa. Crie um novo disparo
          para começar.
        </p>
        <Button
          label="Criar novo disparo"
          icon="i-lucide-plus"
          color="blue"
          @click="navigateToNewBroadcast"
        />
      </div>

      <!-- Broadcasts list -->
      <div v-else>
        <div
          class="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
        >
          <h2
            class="text-lg sm:text-xl font-medium text-n-slate-12 dark:text-n-slate-12"
          >
            Histórico de Disparos
          </h2>

          <div class="flex items-center gap-2">
            <Button
              label="Atualizar"
              icon="i-lucide-refresh-cw"
              variant="outline"
              color="slate"
              size="sm"
              @click="fetchBroadcasts"
            />
          </div>
        </div>

        <!-- Mobile card view -->
        <div class="block sm:hidden space-y-3">
          <div
            v-for="broadcast in filteredBroadcasts"
            :key="broadcast.id"
            class="bg-white dark:bg-n-slate-2 border border-n-slate-3 dark:border-n-slate-7 rounded-lg p-4 hover:shadow-md dark:hover:shadow-lg transition-shadow cursor-pointer"
            @click="viewBroadcastDetails(broadcast.id)"
          >
            <div class="flex justify-between items-start mb-3">
              <div>
                <div
                  class="text-sm font-medium text-n-slate-12 dark:text-n-slate-12"
                >
                  {{ formatDate(broadcast.created_at) }}
                </div>
                <div class="text-xs text-n-slate-9 dark:text-n-slate-11">
                  {{ formatTime(broadcast.created_at) }}
                </div>
              </div>
              <div class="flex items-center gap-3">
                <i
                  :class="[getInboxTypeFormatted(broadcast.integration_type).icon, getInboxTypeFormatted(broadcast.integration_type).color, 'text-lg']"
                />
              </div>
            </div>

            <div class="space-y-2">
              <div class="flex justify-between items-center">
                <span class="text-xs text-n-slate-11">Destinatários:</span>
                <span class="text-sm font-medium text-n-slate-12">{{
                  getRecipientCount(broadcast)
                }}</span>
              </div>

              <div class="w-full h-2 bg-n-slate-3 dark:bg-n-slate-7 rounded-full">
                <div
                  class="h-full rounded-full"
                  :class="getProgressBarClass(broadcast)"
                  :style="{ width: `${getCompletionPercentage(broadcast)}%` }"
                />
              </div>

              <div class="flex justify-between items-center">
                <div class="flex items-center gap-3">
                  <div class="text-xs text-green-600 dark:text-green-400">
                    <i class="i-lucide-check-circle mr-0.5" />
                    {{ getSuccessCount(broadcast) }}
                  </div>
                  <div class="text-xs text-n-ruby-11 dark:text-n-ruby-10">
                    <i class="i-lucide-x-circle mr-0.5" />
                    {{ getFailureCount(broadcast) }}
                  </div>
                </div>

                <div class="flex gap-1">
                  <Button
                    icon="i-lucide-eye"
                    variant="outline"
                    color="slate"
                    size="xs"
                    @click.stop="viewBroadcastDetails(broadcast.id)"
                  />
                  <Button
                    icon="i-lucide-repeat"
                    variant="outline"
                    color="amber"
                    size="xs"
                    @click.stop="retryBroadcast(broadcast.id)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Desktop table view -->
        <div
          class="hidden sm:block overflow-x-auto border border-n-slate-3 dark:border-n-slate-7 rounded-lg"
        >
          <table
            class="min-w-full divide-y divide-n-slate-3 dark:divide-n-slate-7"
          >
            <thead class="bg-n-slate-2 dark:bg-n-slate-4">
              <tr>
                <th
                  scope="col"
                  class="px-3 sm:px-6 py-3 text-left text-xs font-medium text-n-slate-9 dark:text-n-slate-11 uppercase tracking-wider"
                >
                  Data
                </th>
                <th
                  scope="col"
                  class="hidden sm:table-cell px-6 py-3 text-left text-xs font-medium text-n-slate-9 dark:text-n-slate-11 uppercase tracking-wider"
                >
                  Tipo
                </th>
                <th
                  scope="col"
                  class="px-3 sm:px-6 py-3 text-left text-xs font-medium text-n-slate-9 dark:text-n-slate-11 uppercase tracking-wider"
                >
                  Destinatários
                </th>
                <th
                  scope="col"
                  class="hidden md:table-cell px-6 py-3 text-left text-xs font-medium text-n-slate-9 dark:text-n-slate-11 uppercase tracking-wider"
                >
                  Status
                </th>
                <th
                  scope="col"
                  class="px-3 sm:px-6 py-3 text-left text-xs font-medium text-n-slate-9 dark:text-n-slate-11 uppercase tracking-wider"
                >
                  Resultado
                </th>
                <th
                  scope="col"
                  class="px-3 sm:px-6 py-3 text-right text-xs font-medium text-n-slate-9 dark:text-n-slate-11 uppercase tracking-wider"
                >
                  Ações
                </th>
              </tr>
            </thead>
            <tbody
              class="bg-white dark:bg-n-slate-2 divide-y divide-n-slate-3 dark:divide-n-slate-7"
            >
              <tr
                v-for="broadcast in filteredBroadcasts"
                :key="broadcast.id"
                class="hover:bg-n-slate-1 dark:hover:bg-n-slate-3 cursor-pointer"
                @click="viewBroadcastDetails(broadcast.id)"
              >
                <td class="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                  <div
                    class="text-xs sm:text-sm text-n-slate-12 dark:text-n-slate-12"
                  >
                    {{ formatDate(broadcast.created_at) }}
                  </div>
                  <div class="text-xs text-n-slate-9 dark:text-n-slate-11">
                    {{ formatTime(broadcast.created_at) }}
                  </div>
                </td>
                <td class="hidden sm:table-cell px-6 py-4 whitespace-nowrap">
                  <div class="flex items-center">
                    <i
                      :class="[
                        getInboxTypeFormatted(broadcast.integration_type).icon,
                        getInboxTypeFormatted(broadcast.integration_type).color,
                        'text-lg'
                      ]"
                    />
                    <div class="ml-2">
                      <div
                        class="text-sm font-medium text-n-slate-12 dark:text-n-slate-12"
                      >
                        {{ getInboxTypeFormatted(broadcast.integration_type).displayName }}
                      </div>
                      <div
                        v-if="broadcast.template_name"
                        class="text-xs text-n-slate-9 dark:text-n-slate-11"
                      >
                        Template: {{ broadcast.template_name }}
                      </div>
                    </div>
                  </div>
                </td>
                <td class="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                  <div
                    class="text-xs sm:text-sm text-n-slate-12 dark:text-n-slate-12"
                  >
                    {{ getRecipientCount(broadcast) }}
                    <span class="hidden sm:inline">destinatários</span>
                  </div>
                  <div
                    class="w-full h-1.5 bg-n-slate-3 dark:bg-n-slate-7 rounded-full mt-1"
                  >
                    <div
                      class="h-full rounded-full"
                      :class="getProgressBarClass(broadcast)"
                      :style="{ width: `${getCompletionPercentage(broadcast)}%` }"
                    />
                  </div>
                </td>
                <td class="hidden md:table-cell px-6 py-4 whitespace-nowrap">
                  <span
                    class="text-xs leading-5 font-medium"
                    :class="getStatusClass(broadcast)"
                  >
                    {{ formatStatus(broadcast.status) }}
                  </span>
                </td>
                <td class="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                  <div class="flex items-center">
                    <div
                      class="text-xs text-green-600 dark:text-green-400 mr-1 sm:mr-2"
                    >
                      <i class="i-lucide-check-circle mr-0.5 sm:mr-1 text-xs" />
                      {{ getSuccessCount(broadcast) }}
                    </div>
                    <div class="text-xs text-n-ruby-11 dark:text-n-ruby-10">
                      <i class="i-lucide-x-circle mr-0.5 sm:mr-1 text-xs" />
                      {{ getFailureCount(broadcast) }}
                    </div>
                  </div>
                </td>
                <td
                  class="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-right text-sm font-medium"
                >
                  <div class="flex justify-end gap-1 sm:gap-2">
                    <Button
                      icon="i-lucide-eye"
                      variant="outline"
                      color="slate"
                      size="xs"
                      @click.stop="viewBroadcastDetails(broadcast.id)"
                    />
                    <Button
                      icon="i-lucide-repeat"
                      variant="outline"
                      color="amber"
                      size="xs"
                      class="hidden sm:inline-flex"
                      @click.stop="retryBroadcast(broadcast.id)"
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div class="mt-4">
          <ContactsPagination
            :current-page="pagination.currentPage"
            :total-pages="pagination.totalPages"
            :page-size="pagination.pageSize"
            :page-size-options="pageSizeOptions"
            :show-page-size-selector="true"
            :loading="isLoading"
            :current-items-count="broadcasts.length"
            :total-items-count="totalCount"
            item-name="disparo"
            @page-change="handlePageChange"
            @page-size-change="handlePageSizeChange"
          />
        </div>
      </div>
    </template>

    <!-- Retry Broadcast Modal -->
    <RetryBroadcastModal
      :show="showRetryModal"
      :broadcast="selectedBroadcast"
      @close="showRetryModal = false"
      @proceed="handleRetryProceed"
    />
  </div>
</template>

<style scoped>
/* Notification transitions */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-down-enter-from {
  opacity: 0;
  transform: translateY(-100px) scale(0.95);
}

.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-100px) scale(0.95);
}

.slide-down-enter-to,
.slide-down-leave-from {
  opacity: 1;
  transform: translateY(0) scale(1);
}
</style>
